import { useEffect, useState } from "react";
import { fetchAmazonHistory } from "../../api/client";
import type { AmzHistoryRow } from "../../types/amazon";
import { fmtJst, truncateText } from "../../lib/amazonContent";
import { StateBanner } from "../../components/StateBanner";
import { Badge } from "./AmzUi";
import { SOURCE_TONE, errorMessage } from "./helpers";

interface HistoryTableProps {
  rows: AmzHistoryRow[];
  /** true=全商品（SKU列あり）/ false=1商品 */
  showSku: boolean;
}

export function HistoryTable({ rows, showSku }: HistoryTableProps) {
  const cls = showSku ? "all" : "product";
  return (
    <div className="amz-table-wrap">
      <div className="amz-hist-table">
        <div className={`amz-hist-grid ${cls} head`}>
          <span>日時</span>
          {showSku && <span>SKU</span>}
          <span>ユーザー</span>
          <span>ソース</span>
          <span>項目</span>
          <span>変更前</span>
          <span>変更後</span>
          <span>Amazon</span>
        </div>
        {rows.map((h) => (
          <div key={h.id} className={`amz-hist-grid ${cls}`}>
            <span className="amz-mono">{fmtJst(h.createdAt)}</span>
            {showSku && <span className="amz-mono">{h.sku}</span>}
            <span>{h.user}</span>
            <span className="badge-cell">
              <Badge tone={SOURCE_TONE[h.source]} size="sm" mono>
                {h.source}
              </Badge>
            </span>
            <span style={{ fontWeight: 500 }}>{h.field}</span>
            <span className="old" title={h.oldValue ?? ""}>
              {h.oldValue ? truncateText(h.oldValue, 60) : "—"}
            </span>
            <span title={h.newValue ?? ""}>{h.newValue ? truncateText(h.newValue, 60) : "—"}</span>
            <span className={h.result === "成功" ? "amz-res-ok" : "amz-res-ng"}>{h.result}</span>
          </div>
        ))}
        {rows.length === 0 && <div className="amz-empty">履歴はまだありません</div>}
      </div>
    </div>
  );
}

/** 履歴を取得して表示する。version が変わると再取得する。 */
export function HistoryLoader({ sku, version }: { sku?: string; version?: number }) {
  const [rows, setRows] = useState<AmzHistoryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    fetchAmazonHistory({ sku, limit: 100 })
      .then((r) => {
        if (!cancelled) setRows(r.rows);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(errorMessage(e));
      });
    return () => {
      cancelled = true;
    };
  }, [sku, version, nonce]);

  if (error) return <StateBanner kind="error" message={error} onRetry={() => setNonce((n) => n + 1)} />;
  if (!rows) return <StateBanner kind="loading" message="履歴を取得しています。" />;
  return <HistoryTable rows={rows} showSku={!sku} />;
}
